import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Banknote } from 'lucide-react';
import type { CurrencyToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { serverMessage } from '~/lib/api/server-error';
import { useCurrenciesList, useCurrencyMutations } from '~/hooks/use-currencies';
import { tokenToForm, type CurrencyFormValues } from '~/lib/schemas/currency';
import { CurrencyEditDialog } from './currency-edit-dialog';

/** Currencies (Νομίσματα) CRUD surface — parity with the legacy admin grid (Name/ISO-3/Format/Visible/Sort). */
export function CurrenciesGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useCurrenciesList();
	const { remove } = useCurrencyMutations();

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<CurrencyFormValues | null>(null);
	const [deleteRow, setDeleteRow] = useState<CurrencyToken | null>(null);

	const columns = useMemo<SystemGridColumn<CurrencyToken>[]>(
		() => [
			{
				key: 'name',
				header: t('prices.currencies.columns.name'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.nm ?? '',
				exportValue: o => o.nm ?? '',
				render: o => (
					<span className="truncate text-sm font-medium text-foreground" title={o.nm ?? ''}>
						{o.nm || '—'}
					</span>
				)
			},
			{
				key: 'iso',
				header: t('prices.currencies.columns.iso'),
				width: '120px',
				sortValue: o => o.iso ?? '',
				exportValue: o => o.iso ?? '',
				render: o => <span className="font-mono text-xs font-medium text-foreground">{o.iso}</span>
			},
			{
				key: 'format',
				header: t('prices.currencies.columns.format'),
				width: '180px',
				sortValue: o => o.fs ?? '',
				exportValue: o => o.fs ?? '',
				render: o => <span className="font-mono text-xs text-foreground-muted">{o.fs}</span>
			},
			{
				key: 'visible',
				header: t('prices.currencies.columns.visible'),
				width: '90px',
				align: 'center',
				sortValue: o => (o.iv ? 1 : 0),
				exportValue: o => (o.iv ? t('common.yes') : t('common.no')),
				render: o => (o.iv ? <Check size={16} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>)
			},
			{
				key: 'sort',
				header: t('prices.currencies.columns.sort'),
				width: '80px',
				align: 'right',
				sortValue: o => o.si ?? 0,
				exportValue: o => o.si ?? 0,
				render: o => <span className="text-sm tabular-nums text-foreground-muted">{o.si}</span>
			}
		],
		[t]
	);

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: err => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<SystemGrid<CurrencyToken>
				gridId="currencies"
				title={t('prices.currencies.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.iso}
				columns={columns}
				searchPredicate={(o, q) => `${o.nm ?? ''} ${o.iso ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'sort', dir: 'asc' }}
				emptyIcon={<Banknote size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				addLabel={t('prices.currencies.add')}
				onAdd={() => {
					setEditing(null);
					setEditOpen(true);
				}}
				onEdit={o => {
					setEditing(tokenToForm(o));
					setEditOpen(true);
				}}
				onDelete={setDeleteRow}
				exportFilename="currencies"
			/>

			<CurrencyEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />

			<Dialog open={Boolean(deleteRow)} onOpenChange={open => !open && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('prices.currencies.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('prices.currencies.deleteBody', { name: deleteRow?.nm || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
